import Typography from '@components/v2/ui/Typography/Typography';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import classNames from 'classnames';
import { useRef } from 'react';
import MoneyRendererV3 from '../../../../components/Money/MoneyRendererV3/MoneyRendererV3';
import ArrowScroller from '../../../../components/Scroller/ArrowScroller';
import useTeceoStyles from '../../../../useTeceoStyles';
import { ProductPricesV2Props } from '../interfaces/productPricesV2.interfaces';
import { useProductPricesV2Styles } from '../styles/productPricesV2.styles';
import ReducedProductPricesV2 from './ReducedProductPricesV2';

const TABLE_CELL_WIDTH = 80;

const ProductPricesV2 = ({ prices, hasPriceVariation }: ProductPricesV2Props) => {
  const { classes } = useProductPricesV2Styles();
  const { classes: teceoClasses } = useTeceoStyles();
  const pricesWithPriceComposition = prices?.filter((price) => price.priceComposition?.length);
  const hasDifferentPriceCompositions = prices.some(
    (price) => price.priceComposition?.length !== prices[0].priceComposition?.length,
  );
  const showReducedTooltip =
    !hasPriceVariation &&
    !hasDifferentPriceCompositions &&
    pricesWithPriceComposition.length === prices.length;

  const compositionsLength = prices?.map((p) => p.priceComposition?.length || 0) || [];
  const maxCompositionsLength = Math.max(...compositionsLength);
  const hasCompositions = maxCompositionsLength > 0;
  const lastRowIndex = maxCompositionsLength - 1;

  const containerRef = useRef<HTMLDivElement | null>(null);

  if (showReducedTooltip && !!prices[0].priceComposition) {
    return <ReducedProductPricesV2 priceCompositions={prices[0].priceComposition} />;
  }

  return (
    <ArrowScroller containerRef={containerRef} scrollAmount={TABLE_CELL_WIDTH * 2}>
      <TableContainer
        className={classNames({
          [classes.root]: true,
          [teceoClasses.hideScrollbar]: true,
        })}
        ref={containerRef}
      >
        <Table size="small">
          <TableHead>
            <TableRow className={classes.firstRow}>
              {prices?.map((price, index) => (
                <TableCell key={price.sizeName} className={classes.tableCell}>
                  <Typography key={index} variant="body2">
                    {price.sizeName}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow className={!hasCompositions ? classes.lastRow : ''}>
              {prices?.map((price) => (
                <TableCell key={price.sizeName} className={classes.tableCell}>
                  <MoneyRendererV3
                    value={Number(price.wholesalePrice)}
                    promotionValue={Number(price.promotionalPrice)}
                    size="small"
                    horizontalAlign="right"
                  />
                </TableCell>
              ))}
            </TableRow>

            {[...Array(maxCompositionsLength)].map((_, rowIndex) => (
              <TableRow key={rowIndex} className={rowIndex === lastRowIndex ? classes.lastRow : ''}>
                {prices?.map((price) => {
                  const composition = price.priceComposition?.[rowIndex];
                  return (
                    <TableCell key={price.sizeName} className={classes.tableCell}>
                      {composition ? (
                        <>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.priceCompositionLabel}
                          >
                            {composition.label}
                          </Typography>
                          <MoneyRendererV3
                            value={Number(composition.price)}
                            size="small"
                            horizontalAlign="right"
                          />
                        </>
                      ) : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ArrowScroller>
  );
};

export default ProductPricesV2;
